/* Keuzehulp - dedicated page version (StemWijzer-stijl, inline op /pages/keuzehulp)
 *
 * Rendering target: <div data-keuzehulp-stage> binnen <keuzehulp-section>.
 * Data: 3 quiz-definities + 3 product pools + 1 settings, allemaal embedded JSON-scripts.
 *
 * Stages:
 *   picker   → kies welke keuzehulp (3 cards)
 *   intro    → quiz titel + intro + start
 *   question → 1 vraag tegelijk + progress bar + ← Vorige / Volgende →
 *   results  → matched products (sorted on score)
 *   builder  → noodpakket-categorie-stap (10x)
 *   summary  → noodpakket-eindscherm
 *
 * Default quiz kan komen uit:
 *   - URL param ?quiz=noodradio
 *   - section.settings.default_quiz
 *   - anders: 'picker'
 */

(() => {
  'use strict';

  const STORAGE_PREFIX = 'rr_keuzehulp_';
  const TRACK = (event, payload = {}) => {
    if (typeof gtag === 'function') gtag('event', event, payload);
  };

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent.trim()); } catch (e) {
      console.warn('[keuzehulp] JSON parse error', e, el?.dataset);
      return null;
    }
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function formatTag(t) {
    return t.replace(/^[^:]+:/, '').replace(/-/g, ' ');
  }

  /* ─── Scoring ───────────────────────────────────────────────────── */
  // mode: 'strict' = hard filter via requireTags/excludeTags/maxPrice
  //       'loose'  = altijd alle producten, alleen op tag-overlap scoren (fallback)
  function scoreProducts(products, answers, quiz, mode = 'strict') {
    const results = [];
    for (const p of products) {
      const tagSet = new Set(p.tags || []);
      let score = 0;
      let maxPrice = null;
      const matchedTags = [];
      let disqualified = false;

      for (const q of quiz.questions) {
        const picked = answers[q.id];
        if (!picked || picked.length === 0) continue;
        for (const idx of picked) {
          const opt = q.options[idx];
          if (!opt) continue;

          if (mode === 'strict') {
            if (opt.requireTags) {
              for (const t of opt.requireTags) {
                if (!tagSet.has(t)) { disqualified = true; break; }
              }
            }
            if (opt.requireTagsAny) {
              if (!opt.requireTagsAny.some(t => tagSet.has(t))) disqualified = true;
            }
            if (opt.excludeTags) {
              for (const t of opt.excludeTags) {
                if (tagSet.has(t)) { disqualified = true; break; }
              }
            }
            if (opt.maxPrice != null) {
              maxPrice = maxPrice == null ? opt.maxPrice : Math.min(maxPrice, opt.maxPrice);
            }
            if (disqualified) break;
          }

          const weight = q.weight || 1;
          const base = opt.score || 0;
          const checkTags = [
            ...(opt.requireTags || []),
            ...(opt.requireTagsAny || []),
            ...(opt.preferTags || []),
          ];
          // Penalty in loose mode: tag aanwezig die in excludeTags zit verlaagt score
          if (mode === 'loose' && opt.excludeTags) {
            for (const t of opt.excludeTags) {
              if (tagSet.has(t)) score -= base * weight;
            }
          }
          let tagsHit = 0;
          for (const t of checkTags) {
            if (tagSet.has(t)) { tagsHit++; if (!matchedTags.includes(t)) matchedTags.push(t); }
          }
          if (checkTags.length === 0) score += base * weight;
          else score += base * weight * (tagsHit / checkTags.length);
        }
        if (disqualified) break;
      }

      if (mode === 'strict' && !disqualified && maxPrice != null) {
        const priceInEuros = (p.price || 0) / 100;
        if (priceInEuros > maxPrice) disqualified = true;
      }
      if (!disqualified) results.push({ product: p, score, matchedTags });
    }
    results.sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title));
    return results;
  }

  function applyOverrides(results, settings, answers, quiz, quizId) {
    if (!settings.overrides || !settings.overrides.length) return results;
    for (const ov of settings.overrides) {
      if (ov.quizId && ov.quizId !== quizId) continue;
      const q = quiz.questions.find(qq => qq.id === ov.questionId);
      if (!q) continue;
      const picked = answers[q.id] || [];
      if (!picked.some(idx => q.options[idx]?.label === ov.optionLabel)) continue;
      const found = results.find(r => r.product.id === ov.productId);
      if (found) found.score += 5;
    }
    results.sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title));
    return results;
  }

  function visibleQuestions(quiz, answers) {
    return quiz.questions.filter(q => {
      if (!q.showIf) return true;
      for (const [refQid, allowedLabels] of Object.entries(q.showIf)) {
        const refQ = quiz.questions.find(qq => qq.id === refQid);
        if (!refQ) continue;
        const picked = answers[refQid] || [];
        const pickedLabels = picked.map(i => refQ.options[i]?.label);
        if (!pickedLabels.some(l => allowedLabels.includes(l))) return false;
      }
      return true;
    });
  }

  /* ─── Cart ──────────────────────────────────────────────────────── */
  async function addToCart(variantId, qty = 1) {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', qty);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map(s => s.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement && cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add', config);
    const data = await res.json();
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'keuzehulp', productVariantId: variantId, cartData: data });
    }
    if (cart && typeof cart.renderContents === 'function') cart.renderContents(data);
    TRACK('add_to_cart', { source: 'keuzehulp', variant_id: variantId });
    return data;
  }

  /* ─── KeuzehulpSection custom element ───────────────────────────── */
  class KeuzehulpSection extends HTMLElement {
    constructor() {
      super();
      this.stage = $('[data-keuzehulp-stage]', this);
      this.settings = readJSON($('script[data-keuzehulp-settings]', this)) || {};
      this.quizzes = {
        noodradio:   readJSON($('script[data-keuzehulp-quiz-noodradio]', this)),
        waterfilter: readJSON($('script[data-keuzehulp-quiz-waterfilter]', this)),
        noodpakket:  readJSON($('script[data-keuzehulp-quiz-noodpakket]', this)),
      };
      this.pools = {
        noodradio:   readJSON($('script[data-keuzehulp-products-noodradio]', this)) || [],
        waterfilter: readJSON($('script[data-keuzehulp-products-waterfilter]', this)) || [],
        noodpakket:  readJSON($('script[data-keuzehulp-products-noodpakket]', this)) || [],
      };
      this.noodpakketOverrides = this.settings.noodpakketOverrides || {};
      this.noodpakketGrids = this.settings.noodpakketGrids || {};
      this.pickerCardOverrides = readJSON($('script[data-keuzehulp-picker-cards]', this)) || [];
      this.state = {};
    }

    connectedCallback() {
      const urlQuiz = new URL(location.href).searchParams.get('quiz');
      const start = urlQuiz || this.settings.defaultQuiz;
      if (start && this.quizzes[start]) {
        this.startQuiz(start);
      } else {
        this.renderPicker();
      }
    }

    startQuiz(quizId) {
      const quiz = this.quizzes[quizId];
      if (!quiz) return this.renderPicker();
      this.state = {
        quizId,
        quiz,
        products: this.pools[quizId] || [],
        stage: quiz.type === 'builder' ? 'builder' : 'intro',
        currentIdx: 0,
        answers: this.restoreAnswers(quizId),
        builderSkipped: [],
      };
      TRACK('view_keuzehulp', { quiz_type: quizId });
      this.render();
    }

    restoreAnswers(quizId) {
      try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + quizId);
        return raw ? JSON.parse(raw) : {};
      } catch { return {}; }
    }
    saveAnswers() {
      try { sessionStorage.setItem(STORAGE_PREFIX + this.state.quizId, JSON.stringify(this.state.answers)); } catch {}
    }

    render() {
      this.stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const s = this.state;
      if (!s.stage) return this.renderPicker();
      switch (s.stage) {
        case 'intro':    return this.renderIntro();
        case 'question': return this.renderQuestion();
        case 'results':  return this.renderResults();
        case 'builder':  return this.renderBuilderStep();
        case 'summary':  return this.renderBuilderSummary();
      }
    }

    /* ─── Picker (3 cards) ──────────────────────────────────────── */
    renderPicker() {
      const defaults = this.settings.pickerCards || [];
      // Customizer overrides → fall back to defaults
      const cards = defaults.map(d => {
        const ov = this.pickerCardOverrides.find(o => o.id === d.id);
        return {
          id: d.id,
          title: (ov && ov.title) || d.title,
          description: (ov && ov.description) || d.description,
          badge: (ov && ov.badge) || d.badge || '',
        };
      });
      this.stage.innerHTML = `
        <div class="keuzehulp-picker">
          ${cards.map(c => `
            <button type="button" class="keuzehulp-picker-card${c.badge ? ' keuzehulp-picker-card--featured' : ''}" data-pick="${c.id}">
              ${c.badge ? `<span class="keuzehulp-picker-card__badge">${escapeHtml(c.badge)}</span>` : ''}
              <span class="keuzehulp-picker-card__title">${escapeHtml(c.title)}</span>
              <span class="keuzehulp-picker-card__desc">${escapeHtml(c.description)}</span>
              <span class="keuzehulp-picker-card__arrow" aria-hidden="true">→</span>
            </button>
          `).join('')}
        </div>
      `;
      $$('[data-pick]', this.stage).forEach(btn => {
        btn.addEventListener('click', () => {
          const q = btn.dataset.pick;
          const url = new URL(location.href);
          url.searchParams.set('quiz', q);
          history.replaceState({}, '', url);
          this.startQuiz(q);
        });
      });
    }

    /* ─── Intro ────────────────────────────────────────────────── */
    renderIntro() {
      const s = this.state;
      this.stage.innerHTML = `
        <div class="keuzehulp-intro">
          <button type="button" class="keuzehulp-back-link" data-back-to-picker>← Andere keuzehulp</button>
          <h2 class="keuzehulp-intro__title">${escapeHtml(s.quiz.title)}</h2>
          <p class="keuzehulp-intro__text">${escapeHtml(s.quiz.intro || '')}</p>
          <button type="button" class="keuzehulp-btn keuzehulp-btn--primary" data-start>Start →</button>
        </div>
      `;
      $('[data-start]', this.stage).addEventListener('click', () => {
        s.stage = 'question'; s.currentIdx = 0;
        this.render();
      });
      $('[data-back-to-picker]', this.stage).addEventListener('click', () => this.backToPicker());
    }

    backToPicker() {
      const url = new URL(location.href);
      url.searchParams.delete('quiz');
      history.replaceState({}, '', url);
      this.state = {};
      this.renderPicker();
    }

    /* ─── Question ─────────────────────────────────────────────── */
    renderQuestion() {
      const s = this.state;
      const vq = visibleQuestions(s.quiz, s.answers);
      let visIdx = vq.findIndex(q => q.id === s.quiz.questions[s.currentIdx]?.id);
      if (visIdx === -1) {
        for (let i = s.currentIdx; i < s.quiz.questions.length; i++) {
          const vi = vq.findIndex(q => q.id === s.quiz.questions[i].id);
          if (vi !== -1) { visIdx = vi; s.currentIdx = i; break; }
        }
        if (visIdx === -1) { s.stage = 'results'; return this.render(); }
      }
      const q = vq[visIdx];
      const pct = Math.round(((visIdx + 1) / vq.length) * 100);
      const isMulti = q.type === 'multi';
      const picked = s.answers[q.id] || [];
      const isLast = visIdx === vq.length - 1;

      this.stage.innerHTML = `
        <div class="keuzehulp-flow">
          <div class="keuzehulp-progress">
            <div class="keuzehulp-progress__track">
              <div class="keuzehulp-progress__bar" style="width:${pct}%"></div>
            </div>
            <span class="keuzehulp-progress__text">Vraag ${visIdx + 1} van ${vq.length}</span>
          </div>
          <div class="keuzehulp-question">
            <h2 class="keuzehulp-question__title">${escapeHtml(q.text)}</h2>
            ${q.help ? `<p class="keuzehulp-question__help">${escapeHtml(q.help)}</p>` : ''}
            <div class="keuzehulp-question__options" role="${isMulti ? 'group' : 'radiogroup'}">
              ${q.options.map((opt, i) => `
                <label class="keuzehulp-option${picked.includes(i) ? ' is-selected' : ''}">
                  <input type="${isMulti ? 'checkbox' : 'radio'}" name="kh-${q.id}" value="${i}" ${picked.includes(i) ? 'checked' : ''} />
                  <span class="keuzehulp-option__label">${escapeHtml(opt.label)}</span>
                </label>
              `).join('')}
            </div>
          </div>
          <nav class="keuzehulp-nav">
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-prev>← Vorige</button>
            <button type="button" class="keuzehulp-btn keuzehulp-btn--primary" data-next ${picked.length === 0 ? 'disabled' : ''}>
              ${isLast ? 'Bekijk resultaat →' : 'Volgende →'}
            </button>
          </nav>
        </div>
      `;

      $$('.keuzehulp-option input', this.stage).forEach(input => {
        input.addEventListener('change', () => {
          const val = parseInt(input.value, 10);
          let arr = s.answers[q.id] || [];
          if (isMulti) {
            if (input.checked) arr = [...new Set([...arr, val])];
            else arr = arr.filter(v => v !== val);
          } else {
            arr = [val];
          }
          s.answers[q.id] = arr;
          this.saveAnswers();
          $$('.keuzehulp-option', this.stage).forEach((el, idx) => {
            el.classList.toggle('is-selected', arr.includes(idx));
          });
          $('[data-next]', this.stage).disabled = arr.length === 0;
          TRACK('keuzehulp_question_answered', { quiz_type: s.quizId, question_id: q.id });
        });
      });

      $('[data-prev]', this.stage).addEventListener('click', () => {
        if (visIdx === 0) { s.stage = 'intro'; }
        else { s.currentIdx = s.quiz.questions.findIndex(qq => qq.id === vq[visIdx - 1].id); }
        this.render();
      });
      $('[data-next]', this.stage).addEventListener('click', () => {
        if (isLast) { s.stage = 'results'; }
        else { s.currentIdx = s.quiz.questions.findIndex(qq => qq.id === vq[visIdx + 1].id); }
        this.saveAnswers();
        this.render();
      });
    }

    /* ─── Results ──────────────────────────────────────────────── */
    renderResults() {
      const s = this.state;
      let results = scoreProducts(s.products, s.answers, s.quiz, 'strict');
      let fallback = false;
      if (results.length === 0) {
        // Geen exacte matches → loose mode zodat we altijd een ranking tonen
        results = scoreProducts(s.products, s.answers, s.quiz, 'loose');
        fallback = true;
      }
      results = applyOverrides(results, this.settings, s.answers, s.quiz, s.quizId);
      const max = this.settings.maxResults || 12;
      results = results.slice(0, max);
      const topScore = results[0]?.score || 1;
      const showPct = this.settings.showMatchPercent;
      const showWhy = this.settings.showWhyExplanation;

      TRACK('view_keuzehulp_results', { quiz_type: s.quizId, n: results.length });

      this.stage.innerHTML = `
        <div class="keuzehulp-flow">
          <div class="keuzehulp-progress">
            <div class="keuzehulp-progress__track">
              <div class="keuzehulp-progress__bar" style="width:100%"></div>
            </div>
            <span class="keuzehulp-progress__text">Resultaat</span>
          </div>
          <div class="keuzehulp-results">
            <h2 class="keuzehulp-results__title">${escapeHtml(this.settings.resultHeading)}</h2>
            <p class="keuzehulp-results__intro">${escapeHtml(fallback ? 'Geen exacte match op al je wensen - dit zijn de producten die er het dichtst bij komen:' : this.settings.resultIntro)}</p>
            ${results.length === 0
              ? `<p class="keuzehulp-results__none">${escapeHtml(this.settings.noMatchText)}</p>`
              : `<ul class="keuzehulp-results__list">
                  ${results.map(r => {
                    const pct = Math.round((r.score / topScore) * 100);
                    return `
                      <li class="keuzehulp-card${!r.product.available ? ' is-sold-out' : ''}">
                        ${showPct ? `<span class="keuzehulp-card__match">${pct}% match</span>` : ''}
                        ${r.product.image ? `<img class="keuzehulp-card__img" src="${r.product.image}" alt="" loading="lazy">` : '<div class="keuzehulp-card__img"></div>'}
                        <div class="keuzehulp-card__body">
                          <a class="keuzehulp-card__title" href="${r.product.url}">${escapeHtml(r.product.title)}</a>
                          <span class="keuzehulp-card__price">${escapeHtml(r.product.price_formatted || '')}</span>
                          ${showWhy && r.matchedTags.length ? `<p class="keuzehulp-card__why">Past omdat: ${r.matchedTags.map(t => escapeHtml(formatTag(t))).join(' · ')}</p>` : ''}
                        </div>
                        <div class="keuzehulp-card__actions">
                          ${r.product.available && r.product.variant_id
                            ? `<button type="button" class="keuzehulp-btn keuzehulp-btn--primary" data-add="${r.product.variant_id}">In winkelmandje</button>`
                            : `<a class="keuzehulp-btn keuzehulp-btn--ghost" href="${r.product.url}">Bekijk product</a>`}
                        </div>
                      </li>
                    `;
                  }).join('')}
                </ul>`
            }
          </div>
          <nav class="keuzehulp-nav">
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-restart>Opnieuw beginnen</button>
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-back-to-picker>Andere keuzehulp</button>
          </nav>
        </div>
      `;

      $$('[data-add]', this.stage).forEach(btn => {
        btn.addEventListener('click', async () => {
          btn.disabled = true;
          btn.textContent = 'Toevoegen…';
          try {
            await addToCart(btn.dataset.add, 1);
            btn.textContent = '✓ Toegevoegd';
          } catch (e) {
            console.error(e);
            btn.textContent = 'Fout - probeer opnieuw';
            btn.disabled = false;
          }
        });
      });
      $('[data-restart]', this.stage).addEventListener('click', () => {
        s.answers = {};
        sessionStorage.removeItem(STORAGE_PREFIX + s.quizId);
        s.stage = 'intro';
        this.render();
      });
      $('[data-back-to-picker]', this.stage).addEventListener('click', () => this.backToPicker());
    }

    /* ─── Builder (noodpakket) ─────────────────────────────────── */
    renderBuilderStep() {
      const s = this.state;
      const cats = s.quiz.categories;
      const catDefault = cats[s.currentIdx];
      const ov = this.noodpakketOverrides[catDefault.id] || {};
      // Merge override op default
      const cat = {
        id: catDefault.id,
        title: ov.title || catDefault.title,
        why: ov.why || catDefault.why,
        recommended: ('recommended' in ov) ? ov.recommended : catDefault.recommended,
        subQuiz: ov.sub_quiz || catDefault.subQuiz,
        collection: ov.collection_handle || catDefault.collection,
      };
      const grid = this.noodpakketGrids[catDefault.id] || [];
      const pct = Math.round(((s.currentIdx + 1) / cats.length) * 100);

      this.stage.innerHTML = `
        <div class="keuzehulp-flow">
          <div class="keuzehulp-progress">
            <div class="keuzehulp-progress__track">
              <div class="keuzehulp-progress__bar" style="width:${pct}%"></div>
            </div>
            <span class="keuzehulp-progress__text">Stap ${s.currentIdx + 1} van ${cats.length}</span>
          </div>
          <div class="keuzehulp-builder">
            <h2 class="keuzehulp-builder__title">
              ${escapeHtml(cat.title)}
              ${cat.recommended ? '<span class="keuzehulp-builder__badge">Aanbevolen door overheid</span>' : ''}
            </h2>
            <div class="keuzehulp-builder__why">${cat.why || ''}</div>

            ${grid.length > 0 ? `
              <div class="keuzehulp-grid">
                ${grid.map(p => `
                  <div class="keuzehulp-grid__card${!p.available ? ' is-sold-out' : ''}">
                    <a href="${p.url}" class="keuzehulp-grid__media">
                      ${p.image ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
                    </a>
                    <div class="keuzehulp-grid__body">
                      <a class="keuzehulp-grid__title" href="${p.url}">${escapeHtml(p.title)}</a>
                      <span class="keuzehulp-grid__price">${escapeHtml(p.price_formatted || '')}</span>
                    </div>
                    ${p.available && p.variant_id
                      ? `<button type="button" class="keuzehulp-btn keuzehulp-btn--primary keuzehulp-grid__add" data-add="${p.variant_id}">+ Toevoegen</button>`
                      : `<a class="keuzehulp-btn keuzehulp-btn--ghost keuzehulp-grid__add" href="${p.url}">Bekijken</a>`}
                  </div>
                `).join('')}
              </div>
              ${cat.collection ? `<a class="keuzehulp-builder__viewall" href="/collections/${cat.collection}" target="_blank" rel="noopener">Bekijk alle producten in deze categorie ↗</a>` : ''}
            ` : ''}

            ${cat.subQuiz ? `
              <div class="keuzehulp-builder__cta">
                <a class="keuzehulp-btn keuzehulp-btn--primary" href="?quiz=${cat.subQuiz}">Hulp nodig bij kiezen? Start sub-keuzehulp</a>
              </div>
            ` : ''}
          </div>
          <nav class="keuzehulp-nav">
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-prev ${s.currentIdx === 0 ? 'disabled' : ''}>← Vorige</button>
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-skip-cat>Heb ik al / sla over</button>
            <button type="button" class="keuzehulp-btn keuzehulp-btn--primary" data-next>Volgende stap →</button>
          </nav>
        </div>
      `;

      $$('[data-add]', this.stage).forEach(btn => {
        btn.addEventListener('click', async () => {
          const orig = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Toevoegen…';
          try {
            await addToCart(btn.dataset.add, 1);
            btn.textContent = '✓ Toegevoegd';
            setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 1500);
          } catch (e) {
            console.error(e);
            btn.textContent = 'Fout';
            btn.disabled = false;
          }
        });
      });
      $('[data-skip-cat]', this.stage).addEventListener('click', () => {
        s.builderSkipped.push({ category: cat.id });
        this.advanceBuilder();
      });
      $('[data-prev]', this.stage).addEventListener('click', () => {
        if (s.currentIdx > 0) { s.currentIdx--; this.render(); }
      });
      $('[data-next]', this.stage).addEventListener('click', () => this.advanceBuilder());
    }

    advanceBuilder() {
      const s = this.state;
      s.currentIdx++;
      if (s.currentIdx >= s.quiz.categories.length) s.stage = 'summary';
      this.render();
    }

    renderBuilderSummary() {
      const s = this.state;
      const tiers = s.quiz.discountTiers || [];
      const cartTotal = (window.cart && window.cart.total_price) ? window.cart.total_price / 100 : 0;
      const nextTier = tiers.find(t => cartTotal < t.threshold);

      this.stage.innerHTML = `
        <div class="keuzehulp-flow">
          <div class="keuzehulp-progress">
            <div class="keuzehulp-progress__track">
              <div class="keuzehulp-progress__bar" style="width:100%"></div>
            </div>
            <span class="keuzehulp-progress__text">Klaar</span>
          </div>
          <div class="keuzehulp-summary">
            <h2>Je noodpakket is klaar</h2>
            <p>Doorlopen: ${s.quiz.categories.length} categorieën · Overgeslagen: ${s.builderSkipped.length}</p>
            ${nextTier ? `<p class="keuzehulp-summary__hint">💡 Voeg nog € ${(nextTier.threshold - cartTotal).toFixed(2)} toe en krijg € ${nextTier.amount} korting</p>` : ''}
            <a class="keuzehulp-btn keuzehulp-btn--primary" href="/cart">Naar winkelmandje</a>
            <button type="button" class="keuzehulp-btn keuzehulp-btn--ghost" data-back-to-picker>Andere keuzehulp</button>
          </div>
        </div>
      `;
      $('[data-back-to-picker]', this.stage).addEventListener('click', () => this.backToPicker());
      TRACK('noodpakket_compleet', { skipped: s.builderSkipped.length });
    }
  }

  customElements.define('keuzehulp-section', KeuzehulpSection);
})();
